import { defineStore } from "pinia";
import { ref } from "vue";
import type { PluginSelector } from "../../models/PluginSelector";
import type { PluginParams } from "../../models/PluginParams";
import instanceJWT, { dedupedGet } from "../../api/apiJwt";
import { SUPPLIER_INFO_PARAM, setPluginLoaded as setPluginLoadedRequest } from "../../api/pluginsApi";
import { useNotifications } from "../useNotifications";
import i18n from "../../../i18n";

export const useFunctionSettingsStore = defineStore("functionSettings", () => {
  const plugins = ref<PluginSelector[]>([]);
  const userPlugins = ref<PluginSelector[]>([]);
  const disabledPlugins = ref<PluginSelector[]>([]);
  const pluginParams = ref<PluginParams[]>([]);
  const loading = ref(false);
  const checkPluginParamsLoading = ref(false);

  const notifications = useNotifications();
  const { notify } = notifications;

  async function fetchAllPlugins() {
    loading.value = true;
    try {
      const response = await instanceJWT.get(`eplex/search/plugin_params/plugins?plugin_type=1&${SUPPLIER_INFO_PARAM}`);
      if (response.data.data.status === "ok" && Array.isArray(response.data.data.items)) {
        plugins.value = response.data.data.items as PluginSelector[];
      }
    } catch (error) {
      console.error("Fetching all plugins error: ", error);
    } finally {
      loading.value = false;
    }
  }

  async function fetchUsersPlugins() {
    loading.value = true;
    try {
      const response = await dedupedGet(`eplex/search/plugin_params/plugins/installed?loaded=1&${SUPPLIER_INFO_PARAM}`);
      if (response.data.data.status === "ok" && Array.isArray(response.data.data.items)) {
        userPlugins.value = response.data.data.items as PluginSelector[];
      }
    } catch (error) {
      console.error("Fetching users plugins error: ", error);
    } finally {
      loading.value = false;
    }
  }

  async function fetchPluginParams(pluginId: string) {
    loading.value = true;
    try {
      const response = await instanceJWT.get(`eplex/search/plugin_params/plugins/${pluginId}`);
      if (response.data.data.status === "ok" && Array.isArray(response.data.data.items)) {
        pluginParams.value = response.data.data.items as PluginParams[];
      }
    } catch (error) {
      console.error("Fetching all plugin params: ", error);
    } finally {
      loading.value = false;
    }
  }

  async function requestNewPluginParams(pluginIds: number[]) {
    loading.value = true;
    try {
      const response = await instanceJWT.post("eplex/search/plugin_params/plugins/new", {
        plugin_ids: pluginIds,
      });
      return response.data.data;
    } catch (error) {
      console.error("Requesting new plugin params error: ", error);
    } finally {
      loading.value = false;
    }
  }

  async function checkPluginParams(pluginId: string, cookie: string, params: Record<string, unknown>) {
    checkPluginParamsLoading.value = true;
    try {
      const response = await instanceJWT.post(`eplex/search/plugin_params/plugins/${pluginId}?v5=true`, {
        cookie,
        params,
      });
      return response.data.data;
    } catch (error) {
      console.error("Checking plugin params error: ", error);
    } finally {
      checkPluginParamsLoading.value = false;
    }
  }

  async function savePluginParams(pluginId: string, cookie: string, params: Record<string, unknown>) {
    loading.value = true;
    try {
      const response = await instanceJWT.put(`eplex/search/plugin_params/plugins/${pluginId}`, { cookie, params });
      return response.data.data;
    } catch (error) {
      console.error("Save plugin params error: ", error);
    } finally {
      loading.value = false;
    }
  }

  /**
   * Признак «Загружать в программу» без обращения к сайту поставщика — так отключают
   * поставщика, который не отвечает или отклонил учётку (CAB-500). `false` — не удалось.
   */
  async function setPluginLoaded(pluginId: number, loaded: boolean): Promise<boolean> {
    loading.value = true;
    try {
      return await setPluginLoadedRequest(pluginId, loaded);
    } catch (error) {
      console.error("Set plugin loaded error: ", error);
      return false;
    } finally {
      loading.value = false;
    }
  }

  function findEmptyRequiredFields(
    params: Record<string, { required?: boolean; value?: unknown; title?: string }> | undefined,
  ): string[] {
    if (!params) return [];
    const result: string[] = [];
    for (const key in params) {
      const param = params[key];
      if (param?.required && (param.value === "" || param.value === null || param.value === undefined)) {
        result.push(param.title ?? key);
      }
    }
    return result;
  }

  function extractParamValues(fullParams: Record<string, { value?: string }>): Record<string, string> {
    const result: Record<string, string> = {};
    for (const key in fullParams) {
      const param = fullParams[key];
      result[key] = param?.value ?? "";
    }
    return result;
  }

  async function fetchDisabledPlugins() {
    loading.value = true;
    try {
      const response = await instanceJWT.get(
        `eplex/search/plugin_params/plugins/installed?loaded=0&${SUPPLIER_INFO_PARAM}`,
      );
      if (response.data.data.status === "ok" && Array.isArray(response.data.data.items)) {
        disabledPlugins.value = response.data.data.items as PluginSelector[];
      }
    } catch (error) {
      console.error("Fetching disabled plugins error: ", error);
    } finally {
      loading.value = false;
    }
  }

  async function refreshPlugins() {
    loading.value = true;
    try {
      const [usersResponse, allResponse, disabledResponse] = await Promise.all([
        dedupedGet(`eplex/search/plugin_params/plugins/installed?loaded=1&${SUPPLIER_INFO_PARAM}`),
        instanceJWT.get(`eplex/search/plugin_params/plugins?plugin_type=1&${SUPPLIER_INFO_PARAM}`),
        instanceJWT.get(`eplex/search/plugin_params/plugins/installed?loaded=0&${SUPPLIER_INFO_PARAM}`),
      ]);

      if (usersResponse.data.data.status === "ok" && Array.isArray(usersResponse.data.data.items)) {
        userPlugins.value = usersResponse.data.data.items as PluginSelector[];
      }
      if (allResponse.data.data.status === "ok" && Array.isArray(allResponse.data.data.items)) {
        plugins.value = allResponse.data.data.items as PluginSelector[];
      }
      if (disabledResponse.data.data.status === "ok" && Array.isArray(disabledResponse.data.data.items)) {
        disabledPlugins.value = disabledResponse.data.data.items as PluginSelector[];
      }
    } catch (error) {
      console.error("Refreshing plugins error: ", error);
    } finally {
      loading.value = false;
    }
  }

  /** Параметры копии: значения исходника поверх дефолтов нового плагина, плюс ключи, которых у нового нет. */
  function mergeDuplicateParams(
    oldParams: Record<string, unknown>,
    newRawParams: Record<string, { default?: unknown } | undefined>,
  ): Record<string, unknown> {
    const merged: Record<string, unknown> = {};
    for (const key in newRawParams) {
      merged[key] = oldParams[key] ?? newRawParams[key]?.default ?? "";
    }
    for (const key in oldParams) {
      if (!(key in merged)) merged[key] = oldParams[key];
    }
    return merged;
  }

  /** «Имя (копия) N» — первый свободный номер среди уже существующих копий. */
  function nextCopyTitle(baseName: string): string {
    const copySuffix = i18n.global.t("errors.copySuffix");
    const prefix = `${baseName} ${copySuffix}`;
    const similarNames = userPlugins.value.filter((p) => p.title?.startsWith(prefix)).map((p) => p.title);
    let copyNumber = 1;
    while (similarNames.includes(`${prefix} ${copyNumber}`)) copyNumber++;
    return `${prefix} ${copyNumber}`;
  }

  /** Имя копии пишем в тот ключ названия, который есть у плагина. */
  function applyDuplicateTitle(params: Record<string, unknown>, title: string): void {
    const titleKey = ["plugin_title", "p0_plugin_title", "p0_title"].find((key) => key in params);
    if (titleKey) params[titleKey] = title;
  }

  async function duplicatePlugin(pluginId: number): Promise<void> {
    loading.value = true;
    try {
      const sourcePlugin = userPlugins.value.find((p) => p.id === pluginId);
      if (!sourcePlugin) throw new Error(i18n.global.t("errors.supplierNotFound"));

      const checkRes = await checkPluginParams(pluginId.toString(), "", {});
      if (checkRes?.status !== "ok") throw new Error(i18n.global.t("errors.supplierParamsError"));

      const oldParams = extractParamValues(checkRes.params);

      const newPluginData = await requestNewPluginParams([pluginId]);

      const pluginInfo = newPluginData?.items?.[pluginId.toString()];
      if (!pluginInfo || pluginInfo.status === "err") {
        const errMessage = i18n.global.t("errors.supplierDuplicate");
        notify(errMessage, "error");

        throw new Error(errMessage);
      }

      const newPluginId = pluginInfo.plugin_id.toString();
      const newParams = mergeDuplicateParams(oldParams, pluginInfo.params);
      const baseName = sourcePlugin.title ?? sourcePlugin.plugin_name ?? "";
      applyDuplicateTitle(newParams, nextCopyTitle(baseName));

      const initNewPlugin = await checkPluginParams(newPluginId, "", {});
      const newCookie = initNewPlugin?.cookie ?? "";

      const checkBeforeSave = await checkPluginParams(newPluginId, newCookie, newParams);
      const emptyRequiredFields = findEmptyRequiredFields(checkBeforeSave?.params);

      if (!checkBeforeSave?.can_save && emptyRequiredFields.length === 0) {
        throw new Error(i18n.global.t("errors.supplierSaveError"));
      }

      if (checkBeforeSave?.can_save) {
        const saveRes = await savePluginParams(newPluginId, newCookie, newParams);
        if (saveRes?.status !== "ok") {
          throw new Error(i18n.global.t("errors.supplierParamsSaveError"));
        }
      }

      await refreshPlugins();

      if (emptyRequiredFields.length > 0) {
        const fieldNames = emptyRequiredFields.join(", ");
        notify(`${i18n.global.t("errors.supplierRequiredFields")}: ${fieldNames}`, "warning");
      }
    } catch (error: unknown) {
      console.error("Duplicate plugin error: ", error);
      const message = error instanceof Error ? error.message : i18n.global.t("errors.supplierDuplicateError");
      throw new Error(message);
    } finally {
      loading.value = false;
    }
  }

  return {
    duplicatePlugin,
    plugins,
    userPlugins,
    disabledPlugins,
    loading,
    pluginParams,
    fetchAllPlugins,
    fetchUsersPlugins,
    fetchDisabledPlugins,
    refreshPlugins,
    fetchPluginParams,
    requestNewPluginParams,
    checkPluginParams,
    savePluginParams,
    setPluginLoaded,
    checkPluginParamsLoading,
  };
});
